import * as D from "date-fns";
import * as React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import _ from "lodash";
import * as d3 from "d3";
import type { ChartOptions } from "billboard.js";
import bb, { spline } from "billboard.js";
import type { Chart } from "billboard.js";
import ReactDOMServer from "react-dom/server";
import { useAtom } from "jotai";
import { GoalAtom, MembersAtom } from "./atoms";
import { formatDecimal, formatMoney } from "./formatters";
import * as strings from "./strings";
import { getCoords } from "./getCoords";
import { useGetMemberFee } from "./useGetMemberFee";
import { useGetMemberCount } from "./useGetMemberCount";
import { useGetProfit } from "./useGetProfit";
import { useRefFunc } from "./useRefFunc";
import { useGetDateFromYearDelta } from "./useGetDateFromYearDelta";
import { useGetGoalReachedYearDelta } from "./useGetGoalReachedYearDelta";
import useResizeObserver from "use-resize-observer";

const {
  legend_goal_label,
  legend_projected_label,
  legend_unchanged_label,
  tooltip_fee_label,
  tooltip_newmembers_label,
  tooltip_totalmembers_label,
  tooltip_missingincome_label
} = strings;

function getColumns(years: number, profit: (x: number) => number) {
  return [
    ["x", ..._.range(0, years + 0.5, 0.5), years + years / 20],
    [
      "data1",
      ..._.range(0, years + 0.5, 0.5).map(x => {
        return profit(x);
      })
    ]
  ];
}

export function ResultChart({
  years,
  initial
}: {
  years: number;
  initial: number;
  startDate: Date;
}) {
  const [members] = useAtom(MembersAtom);

  const getMemberCount = useGetMemberCount();
  const getTotalMembers = useRefFunc<any>(getMemberCount);
  const getMemberFee = useGetMemberFee();
  const getFee = useRefFunc<any>(getMemberFee);
  const getIncome = useGetProfit();
  const getGoalReachedYearDelta = useGetGoalReachedYearDelta();
  const [goal] = useAtom(GoalAtom);

  const goalReachedYearDelta = getGoalReachedYearDelta();
  const getDateFromYearDelta = useGetDateFromYearDelta();
  const dateFromDayNum = getDateFromYearDelta(goalReachedYearDelta);
  const getDateFromYearDeltaRef = useRefFunc(getDateFromYearDelta);

  const g2 = useRefFunc<any>(() => dateFromDayNum);
  const getInitialMembers = useRefFunc<any>(() => members);
  const g3 = useRefFunc<any>(() => goalReachedYearDelta);
  const getIncomeRef = useRefFunc<any>(getIncome);

  const ref = useRef<Chart>();
  useEffect(() => {
    let inRange = goalReachedYearDelta < 0 || goalReachedYearDelta > years;
    let chart = ref.current;
    chart?.config(
      "grid.x.lines",
      !inRange
        ? [{ value: goalReachedYearDelta, class: "uls-green-line" }]
        : [],
      false
    );
  }, [goalReachedYearDelta, years]);
  useEffect(() => {
    let chart = ref.current;
    chart?.config(
      "grid.y.lines",
      [
        { value: goal, class: "uls-green-line" },
        { value: initial, class: "uls-black-line" }
      ],
      false
    );
    chart?.config("axis.y.max", Math.max(initial, goal), false);
    chart?.config("axis.y.min", Math.min(initial, goal), false);
  }, [goal, initial]);

  useEffect(() => {
    let debouncedFunc = _.debounce(
      () =>
        requestIdleCallback(
          () => {
            let chart = ref.current;

            chart?.load({
              columns: getColumns(years, getIncome)
            });

            d3.selectAll(".bb-axis-x .tick text tspan:nth-child(1)").attr(
                "dy",
                "2em"
            );

            d3.selectAll(".bb-axis-x .tick text tspan:nth-child(2)").attr(
                "dy",
                "1.25em"
            );
            {
              let element = document.querySelector(".bb-axis-x");
              let { top, left, width } = getCoords(element);
              d3.select("#clock-icon")
                .style("position", "absolute")
                .style("z-index", 5)
                .style("top", top - 8)
                .style("left", left + width + 8);
            }

            {
              let element = document.querySelector(".bb-axis-y");
              let { top, left, width } = getCoords(element);
              d3.select("#dollar-icon")
                .style("position", "absolute")
                .style("z-index", 5)
                .style("top", top - 16.5 - 8)
                .style("left", left + width - 16.5 / 2);
            }
          },
          { timeout: 100 }
        ),
      32
    );
    debouncedFunc();
    return debouncedFunc.cancel;
  }, [getIncome, goal, years]);

  let options: ChartOptions = React.useMemo(
    () => ({
      bindto: "#chartid",
      spline: { interpolation: { type: "natural" } },
      onrendered() {
        d3.selectAll(".bb-axis-x .tick").each(function(el: number) {
          if (
            g2.current() &&
            getDateFromYearDelta(el) <= g2.current() &&
            getDateFromYearDelta(el + 0.5) > g2.current()
          ) {
            d3.select(this).style("fill", "#53D396");
          } else {
            d3.select(this).style("fill", "black");
          }
        });
        d3.selectAll(".bb-axis-x .tick text tspan:nth-child(1)").attr(
            "dy",
            "2em"
        );

        d3.selectAll(".bb-axis-x .tick text tspan:nth-child(2)").attr(
            "dy",
            "1.25em"
        );
        {
          let element = document.querySelector(".bb-axis-x");
          let { top, left, width } = getCoords(element);
          d3.select("#clock-icon")
            .style("position", "absolute")
            .style("z-index", 5)
            .style("top", top - 8)
            .style("left", left + width + 8);
        }

        {
          let element = document.querySelector(".bb-axis-y");
          let { top, left, width } = getCoords(element);
          d3.select("#dollar-icon")
            .style("position", "absolute")
            .style("z-index", 5)
            .style("top", top - 16.5 - 8)
            .style("left", left + width - 16.5 / 2);
        }
      },
      data: {
        type: spline(),
        x: "x",
        columns: getColumns(years, getIncome),
        colors: {
          data1: "rgb(253, 80, 83)"
        },

        onout() {
          d3.selectAll(".bb-xgrid-line.uls-green-line line").style(
            "display",
            "block"
          );
          d3.selectAll(".bb-axis-x .tick").each(function(el: number) {
            if (
              getDateFromYearDelta(el) <= g2.current() &&
              getDateFromYearDelta(el + 0.5) > g2.current()
            ) {
              d3.select(this).style("fill", "#53D396");
            } else {
              d3.select(this).style("fill", "black");
            }
          });
        },
        onover: function(d) {
          d3.selectAll(".bb-axis-x .tick").each(function(el: number) {
            if (el == d.x) {
              d3.select(this).style("fill", "#E71640");
            } else if (
              g2.current() &&
              getDateFromYearDelta(el) &&
              getDateFromYearDelta(el) <= g2.current() &&
              getDateFromYearDelta(el + 0.5) > g2.current()
            ) {
              d3.select(this).style("fill", "#53D396");
            } else {
              d3.select(this).style("fill", "black");
            }
          });
          if (Math.abs(d.x - g3.current()) < 0.4) {
            d3.selectAll(".bb-xgrid-line.uls-green-line line").style(
              "display",
              "none"
            );
          } else {
            d3.selectAll(".bb-xgrid-line.uls-green-line line").style(
              "display",
              "block"
            );
          }
        }
      },
      point: {
        focus: {
          expand: {
            enabled: false
          }
        },
        pattern: [
          "<g><circle r='3' cx='6' cy='6' stroke='none' fill='#E71640'></circle> <circle r='6' cx='6' cy='6' stroke='#E71640' fill='none' opacity='0.45'></circle></g>"
        ]
      },
      grid: {
        x: {
          show: true
        },
        y: {
          show: true,
          ticks: years * 2,
          lines: [
            { value: goal, class: "uls-green-line" },
            { value: initial, class: "uls-black-line" }
          ]
        },
        front: false
      },
      line: {
        connectNull: true,
        point: ["data1"]
      },
      axis: {
        y: {
          show: true,
          tick: {
            show: false,
            outer: false
          },
          max: Math.max(initial, goal),
          min: Math.min(initial, goal),
          padding: {
            top: 10,
            bottom: 10
          }
        },
        x: {
          height: 5 * 16,
          show: true,
          type: "indexed",
          tick: {
            fit: true,
            format: (i: number) => {
              let date = getDateFromYearDeltaRef.current(i);
              if (date == null) {
                return "";
              }
              return `${D.format(date, "MMM")}\n${D.format(date, "yyyy")}`;
            },
            centered: false,
            show: true,
            outer: false
          },
          padding: {
            left: 0,
            right: 0
          }
        }
      },
      legend: {
        show: false
      },
      tooltip: {
        contents: data => {
          let num = Math.floor(getTotalMembers.current(data[0].x));
          return ReactDOMServer.renderToString(
            <div className={"tooltip"}>
              <div className={"tooltip-row"}>
                <div className="row-left">{tooltip_totalmembers_label}</div>
                <div className="row-right">{formatDecimal(num)}</div>
              </div>
              <div className={"tooltip-row"}>
                <div className="row-left">{tooltip_newmembers_label}</div>
                <div className="row-right">
                  {formatDecimal(num - getInitialMembers.current())}
                </div>
              </div>
              <div className={"tooltip-row"}>
                <div className="row-left">{tooltip_fee_label}</div>
                <div className="row-right">
                  {formatMoney(getFee.current(data[0].x))}
                </div>
              </div>
              <div className={"tooltip-row"}>
                <div className="row-left">{tooltip_missingincome_label}</div>
                <div className="row-right">
                  {formatMoney(getIncomeRef.current(data[0].x) - goal)}
                </div>
              </div>
            </div>
          );
        }
      },
      clipPath: false,
      transition: {
        duration: 30
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useLayoutEffect(() => {
    ref.current = bb.generate(options);
    let chart = ref.current;
    chart?.load({
      columns: getColumns(years, getIncome)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const f = useRef<HTMLDivElement>();
  useResizeObserver({
    ref: f,
    onResize: () => {
      if (ref.current) {
        let { height, width } = f.current?.getBoundingClientRect() ?? {};
        ref.current?.resize({
          height,
          width
        });
      }
    }
  });
  return (
    <>
      <div className="legend">
        <div className="legend-item">
          <span className="color red" />
          <span className="name">{legend_projected_label}</span>
        </div>
        <div className="legend-item">
          <span className="color green" />
          <span className="name">{legend_goal_label}</span>
        </div>
        <div className="legend-item">
          <span className="color grey" />
          <span className="name">{legend_unchanged_label}</span>
        </div>
      </div>
      <div id="chartid" ref={f} />
      <svg
        id="clock-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
      >
        <g id="clock" transform="translate(-1 -1)" opacity="0.3">
          <path
            id="Path_11218"
            data-name="Path 11218"
            d="M9,1a8,8,0,1,0,8,8A8,8,0,0,0,9,1ZM9,15.545A6.545,6.545,0,1,1,15.545,9,6.545,6.545,0,0,1,9,15.545Z"
          />
          <path
            id="Path_11219"
            data-name="Path 11219"
            d="M12.455,9.79V5.727a.727.727,0,1,0-1.455,0v4.364a.727.727,0,0,0,.213.514l2.182,2.182a.727.727,0,1,0,1.028-1.028Z"
            transform="translate(-2.727 -1.091)"
          />
        </g>
      </svg>

      <svg
        id="dollar-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="16.5"
        height="16.5"
        viewBox="0 0 16.5 16.5"
      >
        <g id="dollar" transform="translate(0.25 0.25)" opacity="0.3">
          <path
            id="Path_11216"
            data-name="Path 11216"
            d="M8,16A8,8,0,0,1,2.343,2.343,8,8,0,0,1,13.657,13.657,7.947,7.947,0,0,1,8,16ZM8,1a7,7,0,1,0,7,7A7.008,7.008,0,0,0,8,1Z"
            transform="translate(0 0)"
            stroke="#000"
            strokeWidth="0.5"
          />
          <path
            id="Path_11217"
            data-name="Path 11217"
            d="M186.25,100.5a1.25,1.25,0,1,1,1.25-1.25.5.5,0,0,0,1,0,2.253,2.253,0,0,0-1.75-2.193V96.5a.5.5,0,0,0-1,0v.557a2.25,2.25,0,0,0,.5,4.443,1.25,1.25,0,1,1-1.25,1.25.5.5,0,0,0-1,0,2.253,2.253,0,0,0,1.75,2.193v.557a.5.5,0,0,0,1,0v-.557a2.25,2.25,0,0,0-.5-4.443Z"
            transform="translate(-178.25 -93)"
            stroke="#000"
            strokeWidth="0.5"
          />
        </g>
      </svg>
    </>
  );
}
